/*
 * <oilogic-drawer-nav> — mobile drawer drill-down (Figma slide-in submenus).
 *
 * Split out of component-header.js to keep each header asset under the JS
 * budget. It is an empty controller element (no children) placed inside
 * <oilogic-header>; it queries the drawer links, mega panels and their back
 * buttons through the header so the drawer's DOM/markup is left exactly as the
 * merge shipped it. The parent <oilogic-header> owns the drawer open/close and
 * fires `oilogic:drawer:close`, which resets any open drill level here.
 * Progressive enhancement: without JS the drill links are plain anchors that
 * navigate to their menu URL.
 */
(() => {
  class OilogicDrawerNav extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('oilogic-header');
      if (!this.header) return;

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.panels = new Map();
      this.header.querySelectorAll('[data-mega-panel]').forEach((panel) => {
        this.panels.set(panel.dataset.megaPanel, panel);
      });

      this.drilledKey = null;

      this.header.querySelectorAll('.drawer-nav__link[data-nav-handle]').forEach((link) => {
        const key = link.dataset.navHandle;
        if (!this.panels.has(key)) return;
        link.classList.add('drawer-nav__link--has-panel');
        const arrow = link.querySelector('.drawer-nav__arrow');
        if (arrow) arrow.hidden = false;
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.drill(key, true);
        }, { signal });
      });

      this.header.querySelectorAll('[data-mega-back]').forEach((button) => {
        button.addEventListener('click', () => this.drill(this.drilledKey, false), { signal });
      });

      // The header resets its own drawer state on close; mirror that here so a
      // reopened drawer starts at the top level.
      this.header.addEventListener('oilogic:drawer:close', () => this.reset(), { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    drill(key, open) {
      const panel = key && this.panels.get(key);
      if (!panel) return;
      this.header.classList.toggle('header--drilled', open);
      if (open) {
        panel.hidden = false;
        requestAnimationFrame(() => panel.classList.add('megamenu--open'));
        this.drilledKey = key;
        panel.querySelector('[data-mega-back]')?.focus();
      } else {
        panel.classList.remove('megamenu--open');
        setTimeout(() => { panel.hidden = true; }, 300);
        this.drilledKey = null;
        this.header.querySelector(`.drawer-nav__link[data-nav-handle="${key}"]`)?.focus();
      }
    }

    reset() {
      if (!this.drilledKey) return;
      const panel = this.panels.get(this.drilledKey);
      if (panel) {
        panel.classList.remove('megamenu--open');
        panel.hidden = true;
      }
      this.header.classList.remove('header--drilled');
      this.drilledKey = null;
    }
  }

  if (!customElements.get('oilogic-drawer-nav')) {
    customElements.define('oilogic-drawer-nav', OilogicDrawerNav);
  }
})();
